import { useEffect, useRef } from "react";
import { useLang } from "@/context/LanguageContext";
import { MapPin, Phone, Mail, Globe, Facebook, MoveRight, Sparkles, Play } from "lucide-react";
import { HOTLINE, EMAIL, WEBSITE, FACEBOOK_URL, WHATSAPP_CATALOG } from "@/lib/contact";
import { NALJURI_PHOTOS } from "@/lib/photos";

// Featured cinematic tile — the original hero video, now anchoring the gallery
const FEATURED_VIDEO = NALJURI_PHOTOS.HERO_VIDEO;

// 4K-quality owner photographs only (low-byte JPEGs intentionally excluded)
const GALLERY = [
  NALJURI_PHOTOS.HILLS_FROM_RESORT,
  NALJURI_PHOTOS.SADAPATHOR,
  NALJURI_PHOTOS.NALJURI_JHARNA,
  NALJURI_PHOTOS.BUILDING_DAY,
  NALJURI_PHOTOS.AMENITIES,
  NALJURI_PHOTOS.ROOM_INTERIOR,
  NALJURI_PHOTOS.SHAPLA,
  NALJURI_PHOTOS.NEARBY_HUB,
];

export default function GalleryAndContact() {
  const { t, lang } = useLang();
  const featuredRef = useRef(null);

  // Try to autoplay the featured gallery video (muted) once it scrolls in
  useEffect(() => {
    const v = featuredRef.current;
    if (!v) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) v.play().catch(() => {});
      else v.pause();
    }, { threshold: 0.2 });
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* Gallery */}
      <section id="gallery" className="bg-cream-100" data-testid="gallery-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <p className="eyebrow tag-line">{t.gallery.eyebrow}</p>
              <h2 className={`mt-4 text-4xl md:text-5xl font-display text-forest-700 tracking-tight ${lang==='bn'?'font-bn':''}`}>
                {t.gallery.title}
              </h2>
              <div className="gold-rule mt-6" />
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <a href={FACEBOOK_URL} target="_blank" rel="noreferrer"
                 data-testid="gallery-facebook-link"
                 className="group inline-flex items-center gap-2 text-sm text-gold-600 hover:text-gold-700 transition-colors">
                <Facebook className="w-4 h-4" /> {t.gallery.more_fb}
                <MoveRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {/* FEATURED VIDEO TILE — relocated from hero */}
            <div className="group relative overflow-hidden rounded-md border border-forest-100 md:col-span-2 md:row-span-2"
                 data-testid="gallery-featured-video">
              <video ref={featuredRef}
                     autoPlay muted loop playsInline preload="metadata"
                     poster={NALJURI_PHOTOS.HILLS_FROM_RESORT}
                     className="w-full h-full min-h-[420px] object-cover">
                <source src={FEATURED_VIDEO} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-forest-900/70 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-cream-100/95 text-forest-700 px-3 py-1.5 rounded-full">
                <Play className="w-3 h-3 text-gold-600 fill-gold-600" />
                <span className="text-[10px] uppercase tracking-widest2 font-mono">
                  {lang === 'en' ? 'Cinematic Tour' : 'সিনেম্যাটিক ট্যুর'}
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-cream-100">
                <div className={`text-lg font-display ${lang==='bn'?'font-bn':''}`}>
                  {lang === 'en' ? 'A morning at Naljuri Inn' : 'নলজুরি ইনে এক সকাল'}
                </div>
                <div className="text-[11px] tracking-widest2 uppercase text-sage-200/90 mt-1">
                  {lang === 'en' ? 'Misty hills · Riverside · 4K' : 'কুয়াশা · নদী · ৪কে'}
                </div>
              </div>
            </div>

            {/* High-resolution still photographs */}
            {GALLERY.map((src, i) => (
              <div key={i} className="group overflow-hidden rounded-md border border-forest-100"
                   data-testid={`gallery-img-${i}`}>
                <img src={src}
                     alt={`Naljuri Inn — luxury eco-resort in Jaflong, Sylhet (gallery ${i + 1})`}
                     loading={i < 2 ? 'eager' : 'lazy'}
                     decoding="async"
                     className="w-full h-56 md:h-64 object-cover img-zoom" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="bg-white border-t border-forest-100" data-testid="contact-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="eyebrow tag-line">{t.contact.eyebrow}</p>
              <h2 className={`mt-4 text-4xl md:text-5xl font-display text-forest-700 tracking-tight leading-[1.05] ${lang==='bn'?'font-bn':''}`}
                  data-testid="contact-title">
                {t.contact.title}
              </h2>
              <div className="gold-rule mt-6" />

              <div className="mt-12 space-y-7">
                <ContactRow icon={MapPin} label="Address" value={t.contact.address} />
                <ContactRow icon={Phone} label={t.contact.hotline} value={HOTLINE} href={`tel:${HOTLINE}`} testid="contact-phone" />
                <ContactRow icon={Mail} label={t.contact.email_label} value={EMAIL} href={`mailto:${EMAIL}`} testid="contact-email" />
                <ContactRow icon={Globe} label={t.contact.website} value="www.naljuriinn.com" href={WEBSITE} testid="contact-website" />
                <ContactRow icon={Facebook} label="Facebook" value={t.contact.facebook} href={FACEBOOK_URL} testid="contact-facebook" />
                <ContactRow icon={Sparkles} label="WhatsApp Catalog" value={t.contact.catalog} href={WHATSAPP_CATALOG} testid="contact-catalog" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-md overflow-hidden border border-forest-100 h-[460px] md:h-[560px]">
                <iframe
                  title="Naljuri Inn location"
                  src="https://www.google.com/maps?q=Naljuri+Inn+Jaflong+Sylhet&output=embed"
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const ContactRow = ({ icon: Icon, label, value, href, testid }) => (
  <div className="flex items-start gap-5">
    <div className="w-10 h-10 rounded-full border border-gold-600/40 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-gold-600" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-widest2 text-gold-600 mb-1">{label}</div>
      {href ? (
        <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
           className="text-base text-forest-700 hover:text-gold-700 transition-colors" data-testid={testid}>
          {value}
        </a>
      ) : (
        <div className="text-base text-forest-700" data-testid={testid}>{value}</div>
      )}
    </div>
  </div>
);
